// Product analytics: queues events in memory and sends them to the backend in batches (POST /ev), so
// tracking never blocks the page. Batches go every FLUSH_MS or at MAX_QUEUE events, and with
// navigator.sendBeacon when the page is hidden or closed (a text/plain body: no CORS preflight).
// Failed sends are dropped, never retried. The event dictionary is in ./events.js; the backend side
// (validation, storage, the admin summary) is analytics.py in the backend repo.
//
// Identity: a random visitor id in localStorage (no personal data) and a session that ends after
// SESSION_IDLE_MS without events. Browsers asking not to be tracked (Global Privacy Control / Do Not
// Track) send nothing. Automated browsers are flagged as bots and localhost as dev; the dashboard
// leaves both out.

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu').replace(/\/+$/, '');
const ENDPOINT = `${API_BASE}/ev`;
const VISITOR_KEY = 'ok_usage_client_id'; // kept from the earlier tracker so existing visitors keep their id
const SESSION_KEY = 'ok_usage_session';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 15000;
const MAX_QUEUE = 25;
const MAX_BATCH = 50;
const MAX_ERRORS_PER_PAGE = 5;

const state = {
    started: false,
    enabled: false,
    queue: [],
    seq: 0,
    flushTimer: null,
    ctx: { view: '', lang: '', theme: '' },
    visibleSince: null, // when the page last became visible (active-time tracking)
    viewSince: null,
    errors: 0,
};

const randomId = (prefix) => {
    const random = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    return `${prefix}_${random}`.slice(0, 64);
};

const storage = () => {
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

const readJson = (key) => {
    try {
        return JSON.parse(storage()?.getItem(key) || 'null');
    } catch (error) {
        return null;
    }
};

const writeJson = (key, value) => {
    try {
        storage()?.setItem(key, JSON.stringify(value));
    } catch (error) {
        // Storage full or blocked: tracking still works for this page.
    }
};

let memoryVisitorId = null;
const visitorId = () => {
    const store = storage();
    try {
        const existing = store?.getItem(VISITOR_KEY);
        if (existing && /^[A-Za-z0-9_-]{6,64}$/.test(existing)) return existing;
        const created = randomId('v');
        store?.setItem(VISITOR_KEY, created);
        return created;
    } catch (error) {
        memoryVisitorId = memoryVisitorId || randomId('v');
        return memoryVisitorId;
    }
};

// {id, last, activeMs, isNew}: a session continues across reloads and tabs until it has been idle for
// SESSION_IDLE_MS.
let memorySession = null;
const currentSession = ({ touch = true } = {}) => {
    const now = Date.now();
    const stored = readJson(SESSION_KEY) || memorySession;
    let session = stored;
    let isNew = false;
    if (!session || !session.id || now - (session.last || 0) > SESSION_IDLE_MS) {
        session = { id: randomId('s'), last: now, activeMs: 0 };
        isNew = true;
    }
    if (touch) session.last = now;
    memorySession = session;
    writeJson(SESSION_KEY, session);
    return { ...session, isNew };
};

const addActiveTime = (ms) => {
    if (!(ms > 0)) return;
    const session = currentSession({ touch: false });
    session.activeMs = (session.activeMs || 0) + ms;
    delete session.isNew;
    memorySession = session;
    writeJson(SESSION_KEY, session);
};

const deviceClass = () => {
    const width = window.innerWidth || 0;
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
    if (width < 700) return 'mobile';
    if (coarse && width < 1200) return 'tablet';
    return 'desktop';
};

const isBot = () => Boolean(navigator.webdriver)
    || /bot|crawl|spider|slurp|headless|lighthouse|pagespeed/i.test(navigator.userAgent || '');

const optedOut = () => navigator.globalPrivacyControl === true
    || navigator.doNotTrack === '1' || window.doNotTrack === '1';

// The owner's own browser: open the site once with ?intern=1 (?intern=0 undoes it). Its events are tagged
// dev, like localhost, so the dashboard leaves them out.
const INTERNAL_KEY = 'ok_internal';
const isInternal = () => {
    try {
        const flag = new URLSearchParams(window.location.search).get('intern');
        if (flag === '1') window.localStorage.setItem(INTERNAL_KEY, '1');
        if (flag === '0') window.localStorage.removeItem(INTERNAL_KEY);
        return window.localStorage.getItem(INTERNAL_KEY) === '1';
    } catch (error) {
        return false;
    }
};

// Read on load, before the app can change the URL.
const INTERNAL = typeof window !== 'undefined' && isInternal();

const environment = () => (
    INTERNAL || /^(localhost|127\.|0\.0\.0\.0|\[::1\])/.test(window.location.hostname) ? 'dev' : 'prod'
);

const send = (events, { beacon = false } = {}) => {
    if (!events.length) return;
    const session = currentSession({ touch: false });
    const body = JSON.stringify({
        v: 1,
        vid: visitorId(),
        sid: session.id,
        ctx: { ...state.ctx, dev: deviceClass(), env: environment(), bot: isBot() ? 1 : 0 },
        ev: events,
    });
    try {
        if (beacon && navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) {
            return;
        }
        fetch(ENDPOINT, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'text/plain' },
            keepalive: true,
            credentials: 'omit',
        }).catch(() => {});
    } catch (error) {
        // Never let tracking break the page.
    }
};

export const flushAnalytics = ({ beacon = false } = {}) => {
    if (state.flushTimer) {
        clearTimeout(state.flushTimer);
        state.flushTimer = null;
    }
    while (state.queue.length) {
        send(state.queue.splice(0, MAX_BATCH), { beacon });
    }
};

const scheduleFlush = () => {
    if (state.queue.length >= MAX_QUEUE) {
        flushAnalytics();
        return;
    }
    if (!state.flushTimer) {
        state.flushTimer = setTimeout(() => {
            state.flushTimer = null;
            flushAnalytics();
        }, FLUSH_MS);
    }
};

// track('listing.open', {source: 'map', ...}): see events.js for the names and props.
export const track = (name, props = {}) => {
    if (!state.enabled) return;
    const session = currentSession();
    if (session.isNew && state.started && name !== 'session.start') startSession();
    state.seq += 1;
    state.queue.push({ n: name, t: Date.now(), s: state.seq, v: state.ctx.view || undefined, p: props || {} });
    scheduleFlush();
};

const referrerDomain = () => {
    try {
        if (!document.referrer) return 'direct';
        const host = new URL(document.referrer).hostname.replace(/^www\./, '');
        return host === window.location.hostname.replace(/^www\./, '') ? 'internal' : host;
    } catch (error) {
        return 'unknown';
    }
};

const viewportBucket = () => {
    const width = window.innerWidth || 0;
    if (width < 480) return '<480';
    if (width < 700) return '480-699';
    if (width < 1024) return '700-1023';
    if (width < 1440) return '1024-1439';
    return '1440+';
};

function startSession() {
    const params = new URLSearchParams(window.location.search);
    track('session.start', {
        referrer: referrerDomain(),
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        landing_view: state.ctx.view || 'other',
        viewport: viewportBucket(),
        returning: Boolean(readJson('ok_usage_seen')),
    });
    writeJson('ok_usage_seen', 1);
}

const endViewTime = () => {
    if (state.viewSince == null || !state.ctx.view) return;
    const activeS = Math.round((Date.now() - state.viewSince) / 1000);
    state.viewSince = null;
    if (activeS > 0) track('view.time', { view: state.ctx.view, active_s: activeS });
};

const onHidden = () => {
    if (state.visibleSince != null) {
        addActiveTime(Date.now() - state.visibleSince);
        state.visibleSince = null;
    }
    endViewTime();
    const session = currentSession({ touch: false });
    track('session.end', { active_s: Math.round((session.activeMs || 0) / 1000) });
    flushAnalytics({ beacon: true });
};

const onVisible = () => {
    state.visibleSince = Date.now();
    state.viewSince = Date.now();
};

// Called once from App with the starting view, language and theme.
export const initAnalytics = (ctx = {}) => {
    if (state.started || typeof window === 'undefined') return;
    state.started = true;
    state.enabled = !optedOut();
    if (!state.enabled) return;
    state.ctx = { ...state.ctx, ...ctx };
    const session = currentSession();
    if (session.isNew) startSession();
    track('view.show', { view: state.ctx.view });
    if (document.visibilityState === 'visible') onVisible();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') onHidden();
        else onVisible();
    });
    window.addEventListener('pagehide', () => {
        if (document.visibilityState !== 'hidden') onHidden();
    });
    const onError = (message, source, line) => {
        if (state.errors >= MAX_ERRORS_PER_PAGE) return;
        state.errors += 1;
        track('error.client', {
            message: String(message || 'unknown').slice(0, 150),
            source: String(source || '').split('/').pop().split('?')[0].slice(0, 60),
            line: Number(line) || undefined,
        });
    };
    window.addEventListener('error', (event) => onError(event.message, event.filename, event.lineno));
    window.addEventListener('unhandledrejection', (event) => onError(`promise: ${event.reason?.message || event.reason}`));
};

// The current view/language/theme go with every event; a view change also records time spent.
export const setAnalyticsContext = (ctx = {}) => {
    const nextView = ctx.view;
    if (nextView && nextView !== state.ctx.view && state.started) {
        const from = state.ctx.view;
        endViewTime();
        state.ctx = { ...state.ctx, ...ctx };
        if (from) track('view.switch', { from, to: nextView });
        track('view.show', { view: nextView });
        state.viewSince = document.visibilityState === 'visible' ? Date.now() : null;
        return;
    }
    state.ctx = { ...state.ctx, ...ctx };
};
